import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectUserInfo,
  selectIsUserAdmin,
  selectIsUserStaff,
  loadUserInfo,
  logoutUser,
} from '../../store/features/user';
import { isTokenValid } from '../../utils/jwt-helper';
import { fetchUserDetails } from '../../api/userInfo';
import { setLoading } from '../../store/features/common';

/**
 * Gate for the /staff area. Mirrors AdminProtectedRoute, but admits both
 * STAFF and ADMIN (the owner can always see what the staff sees). Field
 * visibility (no cost/margin/supplier) is enforced SERVER-side by the
 * /api/staff DTOs — this gate is routing, not data security.
 */
const StaffProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const isUserAdmin = useSelector(selectIsUserAdmin);
  const isUserStaff = useSelector(selectIsUserStaff);
  const location = useLocation();

  const isLoggedIn = isTokenValid();

  useEffect(() => {
    if (isLoggedIn && (!userInfo || !userInfo.id)) {
      dispatch(setLoading(true));
      fetchUserDetails()
        .then((res) => {
          dispatch(loadUserInfo(res));
        })
        .catch((err) => {
          console.error('Failed to load user details:', err);
          const status = err?.response?.status;
          if (status === 401) {
            dispatch(logoutUser());
            window.location.href =
              '/login?redirect=' + encodeURIComponent(window.location.pathname || '/staff');
          }
        })
        .finally(() => {
          dispatch(setLoading(false));
        });
    }
  }, [dispatch, isLoggedIn, userInfo]);

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!userInfo || !userInfo.id) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div
            className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full"
            role="status"
          >
            <span className="sr-only">Cargando...</span>
          </div>
          <p className="mt-4">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isUserStaff && !isUserAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default StaffProtectedRoute;
